import { Suspense, useRef, useEffect, useMemo, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { PerformanceMonitor, AdaptiveDpr } from "@react-three/drei";
import * as THREE from "three";
import { Globe } from "./Globe";
import { Containers } from "./Containers";
import { Particles } from "./Particles";
import { sceneRefs } from "@/state/sceneRefs";

/**
 * Scene 3D background. Persistent, fixed-position canvas behind all content.
 * Scroll-driven camera + globe rotation.
 *
 * Performance:
 *  - DPR clamped (PerformanceMonitor + AdaptiveDpr)
 *  - InstancedMesh for containers, single Points for particles
 *  - Reduced motion → disables frameloop animation, returns null
 */

const SCROLL_RAMPS = [
    // [scrollProgress, cameraPos x,y,z, lookAt x,y,z, globeRotY]
    { p: 0.0, cam: [0, 0.4, 5.5], look: [0, 0, 0], rot: 0 },
    { p: 0.08, cam: [1.4, 0.6, 5.0], look: [0, 0, 0], rot: 0.5 },
    { p: 0.16, cam: [2.2, 0.2, 4.6], look: [-0.4, -0.2, 0], rot: 1.1 },
    { p: 0.32, cam: [-2.0, 0.9, 5.2], look: [0, 0, 0], rot: 2.3 },
    { p: 0.5, cam: [0, 0.0, 4.2], look: [0, 0, 0], rot: 3.4 },
    { p: 0.62, cam: [1.6, -0.3, 4.6], look: [0.2, 0, 0], rot: 4.2 },
    { p: 0.78, cam: [-1.8, 0.6, 5.8], look: [0, 0, 0], rot: 5.0 },
    { p: 0.9, cam: [0.4, 0.2, 6.2], look: [0, 0, 0], rot: 6.0 },
    { p: 1.0, cam: [0, 0.0, 7.0], look: [0, 0, 0], rot: 7.0 },
];

function lerpRamp(progress) {
    let i = 0;
    for (; i < SCROLL_RAMPS.length - 1; i++) {
        if (progress <= SCROLL_RAMPS[i + 1].p) break;
    }
    const a = SCROLL_RAMPS[Math.min(i, SCROLL_RAMPS.length - 2)];
    const b = SCROLL_RAMPS[Math.min(i + 1, SCROLL_RAMPS.length - 1)];
    const span = Math.max(1e-5, b.p - a.p);
    const t = Math.min(1, Math.max(0, (progress - a.p) / span));
    return {
        cam: [
            a.cam[0] + (b.cam[0] - a.cam[0]) * t,
            a.cam[1] + (b.cam[1] - a.cam[1]) * t,
            a.cam[2] + (b.cam[2] - a.cam[2]) * t,
        ],
        look: [
            a.look[0] + (b.look[0] - a.look[0]) * t,
            a.look[1] + (b.look[1] - a.look[1]) * t,
            a.look[2] + (b.look[2] - a.look[2]) * t,
        ],
        rot: a.rot + (b.rot - a.rot) * t,
    };
}

function CameraRig({ scrollRef, mouseRef, globeRotRef }) {
    const lookTarget = useMemo(() => new THREE.Vector3(), []);
    const camTarget = useMemo(() => new THREE.Vector3(), []);
    // Showcase camera: tight, centered close-up on the globe
    const FOCUS_CAM = [0, 0, 3.2];
    const FOCUS_LOOK = [0, 0, 0];

    useFrame((state) => {
        const p = scrollRef.current ?? 0;
        const { cam, look, rot } = lerpRamp(p);

        const focus = Math.min(1, Math.max(0, sceneRefs.globeFocus || 0));
        const boost = sceneRefs.globeBoost || 0;
        globeRotRef.current = rot + boost * 1.5;

        const mx = mouseRef.current.x * 0.4 * (1 - focus * 0.7);
        const my = mouseRef.current.y * 0.2 * (1 - focus * 0.7);

        // Blend regular ramp cam toward FOCUS_CAM by focus amount
        camTarget.set(
            cam[0] * (1 - focus) + FOCUS_CAM[0] * focus + mx,
            cam[1] * (1 - focus) + FOCUS_CAM[1] * focus + my,
            cam[2] * (1 - focus) + FOCUS_CAM[2] * focus,
        );
        lookTarget.set(
            look[0] * (1 - focus) + FOCUS_LOOK[0] * focus,
            look[1] * (1 - focus) + FOCUS_LOOK[1] * focus,
            look[2] * (1 - focus) + FOCUS_LOOK[2] * focus,
        );

        state.camera.position.lerp(camTarget, 0.06);
        state.camera.lookAt(lookTarget);
    });
    return null;
}

export function Scene3D() {
    const scrollRef = useRef(0);
    const globeRotRef = useRef(0);
    const mouseRef = useRef({ x: 0, y: 0 });
    const [enabled, setEnabled] = useState(true);
    const [dpr, setDpr] = useState([1, 1.75]);

    useEffect(() => {
        const reduce = window.matchMedia(
            "(prefers-reduced-motion: reduce)",
        ).matches;
        if (reduce) {
            setEnabled(false);
            return;
        }

        let pending = false;
        const onScroll = () => {
            if (pending) return;
            pending = true;
            requestAnimationFrame(() => {
                const max =
                    (document.documentElement.scrollHeight ||
                        document.body.scrollHeight) - window.innerHeight;
                const y = window.scrollY || 0;
                scrollRef.current = max > 0 ? Math.min(1, y / max) : 0;
                pending = false;
            });
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        window.addEventListener("resize", onScroll);

        const onMouse = (e) => {
            mouseRef.current.x = (e.clientX / window.innerWidth - 0.5) * 2;
            mouseRef.current.y = -(e.clientY / window.innerHeight - 0.5) * 2;
        };
        window.addEventListener("mousemove", onMouse, { passive: true });

        return () => {
            window.removeEventListener("scroll", onScroll);
            window.removeEventListener("resize", onScroll);
            window.removeEventListener("mousemove", onMouse);
        };
    }, []);

    if (!enabled) return null;

    return (
        <div
            data-testid="scene-3d"
            aria-hidden
            className="fixed inset-0 z-0 pointer-events-none"
            style={{ background: "#0B0807" }}
        >
            <Canvas
                gl={{
                    antialias: true,
                    alpha: false,
                    powerPreference: "high-performance",
                }}
                dpr={dpr}
                camera={{ position: [0, 0.4, 5.5], fov: 45, near: 0.1, far: 80 }}
                onCreated={({ gl }) => {
                    gl.setClearColor(new THREE.Color("#0B0807"), 1);
                }}
            >
                <PerformanceMonitor
                    onDecline={() => setDpr([1, 1])}
                    onIncline={() => setDpr([1, 1.75])}
                />
                <AdaptiveDpr pixelated={false} />
                <fog attach="fog" args={["#0B0807", 8, 22]} />
                <ambientLight intensity={0.6} />
                <directionalLight
                    position={[4, 6, 4]}
                    intensity={1.5}
                    color="#ffffff"
                />
                <pointLight
                    position={[-4, -2, -3]}
                    intensity={1.4}
                    color="#D69331"
                    distance={14}
                />
                <pointLight
                    position={[3, 2, 5]}
                    intensity={1.0}
                    color="#ffffff"
                    distance={12}
                />

                <Suspense fallback={null}>
                    <Particles count={1400} />
                    <Containers scrollRef={scrollRef} />
                    <Globe rotationRef={globeRotRef} />
                    <CameraRig
                        scrollRef={scrollRef}
                        mouseRef={mouseRef}
                        globeRotRef={globeRotRef}
                    />
                </Suspense>
            </Canvas>
        </div>
    );
}
